/**
 * Typed HTTP client for the Fantasy Stats Tracker API.
 * Handles error parsing, session cookies, and JSON serialization.
 */

import { ApiError, ErrorResponse } from "../types/index";

const BASE = "/api";

/**
 * Fetch wrapper with error handling, credentials, and JSON serialization.
 */
async function request<T>(
  method: string,
  path: string,
  options?: {
    body?: unknown;
    headers?: Record<string, string>;
  }
): Promise<T> {
  const url = `${BASE}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options?.headers,
  };

  const response = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  // Handle 204 No Content
  if (response.status === 204) {
    return null as any as T;
  }

  // Parse response body
  let data: unknown;
  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  // Check for error status codes
  if (!response.ok) {
    // Try to extract error details from the standard error envelope
    if (
      typeof data === "object" &&
      data !== null &&
      "error" in data &&
      typeof data.error === "object" &&
      data.error !== null
    ) {
      const errorData = data.error as Partial<ErrorResponse>;
      throw new ApiError(
        response.status,
        errorData.code || "unknown_error",
        errorData.message || `HTTP ${response.status}`,
        errorData.field
      );
    }
    // Fallback if error envelope is not present
    throw new ApiError(
      response.status,
      "unknown_error",
      typeof data === "string" ? data : `HTTP ${response.status}`
    );
  }

  return data as T;
}

/**
 * GET request.
 */
export async function get<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

/**
 * POST request with body.
 */
export async function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>("POST", path, { body });
}

/**
 * PATCH request with body.
 */
export async function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>("PATCH", path, { body });
}

/**
 * DELETE request.
 */
export async function del<T>(path: string): Promise<T> {
  return request<T>("DELETE", path);
}
